import { UpsertDeleteModal } from "@/pages/configuration/pages/adjustment-reasons/components/UpsertDeleteModal";
import { ErrorModal } from "@dexteel/mesf-core";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Autocomplete,
	CircularProgress,
	createFilterOptions,
	IconButton,
	TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { Link } from "react-router-dom";
import { AdjustmentReason } from "../models/AdjustmentReason";
import { getAdjustmentReasons } from "../repositories/AdjustmentReasonRepository";

type Props = {
	label: string;
	value: number | null;
	onChange?: Function;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	required?: boolean;
};

const useAdjustmentReasons = (onError?: (error: Error) => void) => {
	return useQuery<AdjustmentReason[], Error>({
		queryKey: ["adjustmentReasons"],
		queryFn: ({ signal }) => getAdjustmentReasons(signal),
		onError: onError,
	});
};

export const AdjustmentReasonSelector = ({
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	required = false,
}: Props) => {
	const [filterText, setFilterText] = useState("");
	const [error, setError] = useState("");
	const [showModal, setShowModal] = useState<"create" | "">("");

	const { data: adjustmentReasons, isLoading } = useAdjustmentReasons((error) =>
		setError(error.message),
	);

	const orderedReasons = (adjustmentReasons ?? []).sort((a, b) => {
		if (a.AdjustmentReasonName === "Other") {
			return 1;
		}
		if (b.AdjustmentReasonName === "Other") {
			return -1;
		}
		return 0;
	});

	const selectedAdjustmentReason =
		adjustmentReasons?.find((reason) => reason.AdjustmentReasonId === value) ||
		null;

	const filter = createFilterOptions<AdjustmentReason>();

	return (
		<>
			<Autocomplete
				id="clear-on-escape"
				clearOnEscape
				options={orderedReasons || []}
				getOptionLabel={(option) => option.AdjustmentReasonName}
				disabled={disabled}
				filterOptions={(options, state) => {
					const filtered = filter(options, state);

					const otherOption = orderedReasons.find(
						(option) => option.AdjustmentReasonName === "Other",
					);

					if (
						otherOption &&
						!filtered.some((option) => option.AdjustmentReasonName === "Other")
					) {
						filtered.push(otherOption);
					}

					return filtered as AdjustmentReason[];
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						variant="outlined"
						fullWidth
						size="small"
						error={!!fieldError}
						helperText={fieldError?.message}
						onChange={(event) => {
							setFilterText(event.target.value);
						}}
						sx={{
							backgroundColor: (theme) => theme.palette.background.paper,
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										<>
											{params.InputProps.endAdornment}
											{value ? (
												<IconButton
													component={Link}
													to={`/configuration/adjustments-reasons/${value}`}
													target="_blank"
													size="small"
													title="See in adjustment reasons"
												>
													<VisibilityIcon fontSize="small" />
												</IconButton>
											) : null}
										</>
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedAdjustmentReason}
				onChange={(event, newValue) => {
					if (newValue?.AdjustmentReasonName == "Other") {
						setShowModal("create");
					} else {
						onChange(newValue?.AdjustmentReasonId ?? null);
					}
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading adjustment reasons"
			/>
			<UpsertDeleteModal
				adjustmentReason={{
					AdjustmentReasonId: null,
					AdjustmentReasonName: filterText,
					IsActive: true,
				}}
				modal={showModal}
				onHide={(id?: number) => {
					setShowModal("");
					onChange(id ?? null);
				}}
			/>
		</>
	);
};
